import { Tag } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../../constant';
import cloneDeep from 'lodash.clonedeep';
import ReactEcharts from '../../Echarts/Echarts';
import { arrayOfObjects } from '../../../Mock/MockJson';
import { ErrorComponent, NoRecordComponent } from '../../Utils';
import { trendLabelTranslation } from '../../../Utils/Adapter/translationAdapter';
import { getChartDataWithoutSelection, getChartDataWithSelection } from '../../ChartCalls';

const Scatter = (props) => {
  const [option, setOption] = useState(getInitialOption());
  const [refreshDateTime, setRefreshDateTime] = useState(moment());
  const [errorInAPI, setErrorInAPI] = useState(false);
  const [noDataInDB, setNoDataInDB] = useState(false);
  let timeTicket = useRef(null);
  const requestCheck = useRef(true);
  const abortController = useRef(new window.AbortController());

  const {
    graphprops,
    selectionDate,
    selectionMonth,
    DeviceCode,
    selectionDateRange,
    dayMonthYear,
    currentTimeZone,
  } = props;

  useEffect(() => {
    if (timeTicket.current) {
      clearInterval(timeTicket.current);
    }
    let refreshTime = graphprops?.refreshTime ? graphprops.refreshTime * 60000 : config.defaultRefreshTime;
    timeTicket.current = setInterval(() => {
      if (requestCheck.current) {
        getData();
      }
    }, refreshTime);
    return () => {
      abortController.current.abort();
      clearInterval(timeTicket.current);
    };
  }, []);

  useEffect(() => {
    getData();
  }, [
    selectionDate,
    selectionMonth,
    selectionDateRange,
    DeviceCode,
    dayMonthYear,
    props.translationCache
  ]);

  function getInitialOption() {
    return {
      legend: {
        textStyle: { color: props.theme.algorithm === "dark" ? 'white' : 'black' },
        data: [{ name: 'test1', icon: 'roundRect' }]
      },
      tooltip: { trigger: 'axis' },
      toolbox: {
        showTitle: false,
        itemSize: 25,
        feature: {
          saveAsImage: {
            backgroundColor: props.theme.algorithm === "dark" ? 'black' : 'white',
            title: 'download',
            name: props.graphprops.title,
            iconStyle: {
              borderColor: '#8c8c8c',
              borderWidth: 2
            }
          }
        }
      },
      xAxis: {
        type: 'category',
        data: props.graphprops,
        axisLine: {
          lineStyle: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          }
        },
        axisTick: {
          lineStyle: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          }
        },
        axisLabel: {
          color: props.theme.algorithm === "dark" ? 'white' : 'black'
        }
      },
      yAxis: {
        axisLine: {
          lineStyle: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          }
        }
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          start: 0,
          end: 100,
          backgroundColor: '#9a9191',
          textStyle: {
            color: 'white'
          }
        },
        {
          type: 'inside',
          start: 0,
          end: 100
        }
      ]
    };
  }

  const getData = useCallback(async () => {
    let json = [];
    requestCheck.current = false;

    if (graphprops && graphprops.enableMock && graphprops.enableMock === true) {
      json = arrayOfObjects;
      requestCheck.current = true;
    } else {
      if (graphprops?.graph === 'rangeBar' && graphprops?.currentDateData !== undefined) {
        json = graphprops.currentDateData;
      } else {
        if (
          graphprops &&
          ((graphprops.DashboardLevelSelection === 'datePicker' ||
            graphprops.GraphLevelSelection === 'datePicker') &&
            selectionDate) ||
          (
            (graphprops.DashboardLevelSelection === 'monthPicker' ||
              graphprops.GraphLevelSelection === 'monthPicker') &&
            selectionMonth) ||
          (
            (graphprops.DashboardLevelSelection === 'dateRangePicker' ||
              graphprops.GraphLevelSelection === 'dateRangePicker') &&
            selectionDateRange) ||
          (
            (graphprops.DashboardLevelSelection === 'dateMonthYearPicker' ||
              graphprops.GraphLevelSelection === 'dateMonthYearPicker') &&
            dayMonthYear) ||
          (
            (graphprops.DashboardLevelSelection === 'deviceSelection' ||
              graphprops.GraphLevelSelection === 'deviceSelection') &&
            DeviceCode)
        ) {
          json = await getChartDataWithSelection({
            graphprops,
            selectionDate,
            selectionMonth,
            selectionDateRange,
            DeviceCode,
            dayMonthYear,
            abortController: abortController.current,
            currentTimeZone,
          });
          requestCheck.current = true;
        } else if (
          graphprops.GraphLevelSelection === 'none' ||
          graphprops.DashboardLevelSelection === 'none'
        ) {
          json = await getChartDataWithoutSelection({
            graphprops,
            abortController: abortController.current,
            DeviceCode,
            TimeZone: currentTimeZone,
          });
          requestCheck.current = true;
        }
      }
    }

    // Handle API response
    if ((json && json.message) || (Array.isArray(json) && json.length === 0 && graphprops.enableMock !== true)) {
      if (json.message) {
        setErrorInAPI(true);
      } else if (json.length === 0) {
        setNoDataInDB(true);
      }
    } else {
      // Process data for chart
      let dates = [];
      let values = [];
      let name = '';
      let tagnames = [];
      let seriesarray = [];
      let legendsdata = [];

      json.forEach((val) => {
        if (val.Description !== name) {
          if (!tagnames.includes(val.Description)) {
            tagnames.push(val.Description);
            name = val.Description;
            legendsdata.push({
              name: val.Description,
              icon: 'roundRect',
              textStyle: {
                color: props.theme.algorithm === "dark" ? 'white' : 'black',
                fontSize: 16,
                fontWeight: 'bold'
              }
            });
          }
        }
      });

      let yAxisData = Array.isArray(graphprops.YAxis) ? [...graphprops.YAxis] : [];
      let yaxis = yAxisData.map((value, index) => ({
        splitLine: {
          show: true,
          lineStyle: {
            color: '#504c4c'
          },
          axisLabel: { fontSize: 15 }
        },
        triggerEvent: true,
        axisLine: {
          lineStyle: {
            color: graphprops.colors[index] !== undefined && value.tags.length > 1
              ? 'white'
              : graphprops.colors[index] || 'white'
          },
          axisLabel: { fontSize: 15 }
        },
        max: value.max,
        min: value.min,
        offset: value.offset,
        interval: value.interval,
        position: value.position,
        name: value.name,
        tags: value.tags
      }));

      yaxis.unshift({
        splitLine: {
          show: true,
          lineStyle: {
            color: '#504c4c'
          }
        },
        triggerEvent: true,
        axisLine: {
          lineStyle: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          }
        },
        axisLabel: { fontSize: 15 },
        tags: []
      });

      tagnames.forEach((tagval, tagindex) => {
        values = [];
        json.forEach((jsonval) => {
          if (tagnames[0] === jsonval.Description && tagindex === 0) {
            let date = graphprops.monthExists === 'true' || graphprops.monthExists === true
              ? moment.utc(jsonval.DateTime).tz(props.currentTimeZone).format('DD/MM/YYYY')
              : moment.utc(jsonval.DateTime).tz(props.currentTimeZone).format('DD/MM/YYYY HH:mm:ss');
            dates.push(date);
          }
          if (tagval === jsonval.Description) {
            values.push(jsonval.Value);
          }
        });

        if (yaxis.length > 1) {
          yaxis.forEach((axis, axisindex) => {
            seriesarray.push({
              name: legendsdata[tagindex]['name'],
              data: values,
              yAxisIndex: axisindex,
              type: 'scatter',
              axisLabel: { fontSize: 15 }
            });
          });
        } else {
          seriesarray.push({
            name: legendsdata[tagindex]['name'],
            data: values,
            type: 'scatter',
            axisLabel: { fontSize: 15 }
          });
        }
      });

      const updatedOption = cloneDeep(getInitialOption());
      const series = await trendLabelTranslation(seriesarray, props.translationCache);
      updatedOption.xAxis.data = [...dates];
      updatedOption.yAxis = [...yaxis];
      updatedOption.series = [...series];
      updatedOption.legend = [...legendsdata];
      setOption(updatedOption);
      setRefreshDateTime(moment());
    }
    setErrorInAPI(false);
    setNoDataInDB(false);
  }, [props]);


  return (
    <Fragment>
      {props.graphprops && props.graphprops.checked === true ? (
        <Tag className="graphTag" color="blue">
          {refreshDateTime ? moment(refreshDateTime).format('YYYY-MM-DD HH:mm:ss') : ''}
        </Tag>
      ) : null}
      {noDataInDB || errorInAPI ? (
        noDataInDB ? (
          <NoRecordComponent />
        ) : (
          <ErrorComponent />
        )
      ) : (
        <ReactEcharts
          option={option}
          notMerge={true}
          lazyUpdate={true}
          style={{ height: '80%', width: '100%' }}
          className="react_for_echarts"
        />
      )}
    </Fragment>
  );
};
const mapStateToProps = (state) => {
  return {
    theme: state.ThemeReducer,
    translationCache: state.TranslationReducer.translationCache,
    currentTimeZone: state.LayoutReducer.currentTimeZone
  };
};

export default connect(mapStateToProps)(Scatter);